import React, { useRef, useState } from "react";

import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/datasets/DatasetTable/FloatingWindow";
import { Callout } from "src/design-system/Callout";
import {
  RawJsonSchemaEditor,
  type RawJsonSchemaEditorRef,
} from "src/jsonSchema/editor/RawJsonSchemaEditor";

export const RawJsonSchemaEditorWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  id,
  data: windowData,
}) => {
  const { close: closeWindow } = useFloatingWindowsActions();
  const { position, windowRef } = useFloatingWindowPosition(computePosition);
  const editorRef = useRef<RawJsonSchemaEditorRef>(null);

  const [isDirty, setIsDirty] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [canSave, setCanSave] = useState(true);

  const handleSave = (rawSchema: string) => {
    windowData.onSave(rawSchema);
    setIsDirty(false);
    closeWindow(id);
  };

  const handleDiscard = () => {
    windowData.onDiscard?.();
    closeWindow(id);
  };

  const handleClose = () => {
    if (isDirty) {
      setShowConfirmModal(true);
    } else {
      handleDiscard();
    }
  };

  const handleChange = (schema: string, dirty: boolean, isValid: boolean) => {
    setIsDirty(dirty);
    setCanSave(isValid);
    windowData.onChange?.(schema, dirty, isValid);
  };

  const handlePrimaryAction = async () => {
    if (canSave) {
      editorRef.current?.save();
      // The actual close will be handled by the handleSave callback
    } else {
      // If invalid, just close the modal and keep editing
      setShowConfirmModal(false);
    }
  };

  const handleConfirmDiscard = () => {
    setShowConfirmModal(false);
    handleDiscard();
  };

  return (
    <>
      <FloatingWindowInternal
        ref={windowRef}
        clipOverflow={false}
        draggable={false}
        height="h-195"
        isPinned={true}
        maximizable={false}
        style={{
          inset: "0px auto auto 0px",
          transform: position,
        }}
        title={
          <div className="mt-1 flex flex-col pl-3 pr-3">
            <div className="text-gray-800 font-inter-semibold-12px">
              {windowData.title}
            </div>
            <div className="font-inter-normal-11px -mt-0.5 text-gray-500">
              {windowData.subtitle}
            </div>
          </div>
        }
        width="w-160"
        onClose={handleClose}
      >
        <RawJsonSchemaEditor
          ref={editorRef}
          initialSchema={windowData.initialSchema}
          topDecoration={
            windowData.infoText && (
              <Callout type="neutral">{windowData.infoText}</Callout>
            )
          }
          onChange={handleChange}
          onSave={handleSave}
        />
      </FloatingWindowInternal>

      <ConfirmationModal
        cancelationButtonText="Discard"
        confirmationButtonText={canSave ? "Save changes" : "Keep editing"}
        description={
          canSave
            ? "Changes made in the JSON view haven't been saved yet. Closing this window will discard them."
            : "The JSON schema is invalid and cannot be saved. You can continue editing or discard your changes."
        }
        open={showConfirmModal}
        title="Unsaved schema changes"
        hideCloseIcon
        onClose={handleConfirmDiscard}
        onConfirm={handlePrimaryAction}
      />
    </>
  );
};
